import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Box, Button, Column, Row, Spinner, TextButton, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { formatPaymentDetailsList, invoiceLogicTextsPath } from '~/business-logic/invoiceLogic';
import { useLocaleKeys } from '~/i18n';
import { invoiceSummaryModel } from '~/models';
import { useClientBilling } from '../../../../hooks/useClientBilling';
import { isInOverdueGroup } from '../../../../hooks/useClientBilling.utils';
import { SectionCard } from '../SectionCard/SectionCard';
import styles from './BillingOverviewCard.module.scss';
import { InvoiceLine } from './InvoiceLine/InvoiceLine';
export const GetBillingOverviewCardTID = (parentTestId)=>getTestIds(parentTestId);
export function BillingOverviewCard(param) {
    let { customerId, isArchived, onManageBilling, onBillClient, onViewUpcomingInvoice, onViewAllOverdue } = param;
    const { billing, isLoading, isError, refetch } = useClientBilling(customerId, {
        refreshOnHookMount: true
    });
    const { upcomingInvoice, latestInvoice, overdueInvoices } = billing;
    const { BillingOverviewCardTestIds: TID } = useMemo(()=>GetBillingOverviewCardTID(), []);
    const { t: texts } = useLocaleKeys('pages.clientDetail.summary.billingOverview');
    const { t: invoiceLogicTexts } = useLocaleKeys(invoiceLogicTextsPath);
    const topOverdue = overdueInvoices[0];
    const overdueReason = topOverdue ? formatPaymentDetailsList(invoiceSummaryModel.paymentDetails(topOverdue), invoiceLogicTexts.paymentDetailLabels, invoiceLogicTexts.labelOnDate) : '';
    return /*#__PURE__*/ _jsx(SectionCard, {
        bi: {
            name: 'client_detail_billing_overview_card'
        },
        testId: TID.Root(),
        title: texts.title(),
        showActions: !isArchived,
        defaultExpanded: true,
        actions: /*#__PURE__*/ _jsxs(Row, {
            gap: 12,
            alignItems: "center",
            children: [
                /*#__PURE__*/ _jsx(TextButton, {
                    bi: {
                        name: 'client_detail_manage_billing'
                    },
                    testId: TID.ManageBillingButton(),
                    variant: "paragraph2",
                    skin: "primary",
                    weight: "normal",
                    underline: "none",
                    onClick: onManageBilling,
                    children: texts.manageBilling()
                }),
                /*#__PURE__*/ _jsx(Box, {
                    className: styles.actionSeparator
                }),
                /*#__PURE__*/ _jsx(TextButton, {
                    bi: {
                        name: 'client_detail_billing_bill_client'
                    },
                    testId: TID.BillClientButton(),
                    variant: "paragraph2",
                    skin: "primary",
                    weight: "normal",
                    underline: "none",
                    onClick: onBillClient,
                    children: texts.billClient()
                })
            ]
        }),
        children: isLoading ? /*#__PURE__*/ _jsx(Box, {
            className: styles.loader,
            children: /*#__PURE__*/ _jsx(Spinner, {})
        }) : isError ? /*#__PURE__*/ _jsxs(Column, {
            gap: 12,
            alignItems: "start",
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    testId: TID.ErrorMessage(),
                    skin: "secondary",
                    children: texts.error.message()
                }),
                /*#__PURE__*/ _jsx(Button, {
                    bi: {
                        name: 'client_detail_billing_overview_retry'
                    },
                    testId: TID.ErrorRetry(),
                    skin: "primaryOutline",
                    onClick: refetch,
                    children: texts.error.retry()
                })
            ]
        }) : /*#__PURE__*/ _jsxs(Column, {
            gap: 20,
            children: [
                !isArchived && /*#__PURE__*/ _jsxs(Column, {
                    gap: 8,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph3",
                            skin: "standard",
                            children: texts.upcomingInvoiceTitle()
                        }),
                        upcomingInvoice == null ? /*#__PURE__*/ _jsx(Typography, {
                            testId: TID.EmptyUpcoming(),
                            variant: "paragraph2",
                            weight: "light",
                            skin: "disabled",
                            children: texts.upcomingInvoiceEmpty()
                        }) : /*#__PURE__*/ _jsx(Box, {
                            testId: TID.UpcomingInvoiceRow(),
                            className: styles.invoiceRow,
                            children: /*#__PURE__*/ _jsxs(Column, {
                                gap: 8,
                                children: [
                                    /*#__PURE__*/ _jsxs(Row, {
                                        gap: 12,
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        children: [
                                            /*#__PURE__*/ _jsx(Typography, {
                                                variant: "paragraph2",
                                                weight: "light",
                                                skin: "secondary",
                                                children: texts.toBeIssuedOn({
                                                    date: upcomingInvoice.issueDate()
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                variant: "paragraph2",
                                                weight: "normal",
                                                children: upcomingInvoice.total()
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(TextButton, {
                                        bi: {
                                            name: 'client_detail_view_upcoming_invoice'
                                        },
                                        testId: TID.UpcomingViewDetailsLink(),
                                        variant: "paragraph2",
                                        skin: "primary",
                                        weight: "normal",
                                        underline: "none",
                                        onClick: onViewUpcomingInvoice,
                                        children: texts.viewDetails()
                                    })
                                ]
                            })
                        })
                    ]
                }),
                latestInvoice != null && !isInOverdueGroup(latestInvoice) && /*#__PURE__*/ _jsxs(Column, {
                    gap: 8,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph3",
                            skin: "standard",
                            children: texts.latestInvoiceTitle()
                        }),
                        /*#__PURE__*/ _jsx(InvoiceLine, {
                            invoice: latestInvoice,
                            customerId: customerId,
                            testId: TID.LatestInvoiceRow(),
                            statusLabel: texts.total(),
                            showDivider: true,
                            hideManagePaymentMethods: isArchived
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(Column, {
                    gap: 8,
                    children: [
                        /*#__PURE__*/ _jsxs(Row, {
                            alignItems: "center",
                            justifyContent: "space-between",
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph3",
                                    skin: "standard",
                                    children: texts.overdueInvoicesTitle()
                                }),
                                overdueInvoices.length > 1 && /*#__PURE__*/ _jsx(TextButton, {
                                    bi: {
                                        name: 'client_detail_overdue_view_all'
                                    },
                                    variant: "paragraph3",
                                    skin: "primary",
                                    weight: "normal",
                                    underline: "none",
                                    onClick: onViewAllOverdue,
                                    children: texts.viewAll()
                                })
                            ]
                        }),
                        topOverdue == null ? /*#__PURE__*/ _jsx(Typography, {
                            testId: TID.EmptyOverdue(),
                            variant: "paragraph2",
                            weight: "light",
                            skin: "disabled",
                            children: texts.overdueInvoicesEmpty()
                        }) : /*#__PURE__*/ _jsx(InvoiceLine, {
                            invoice: topOverdue,
                            customerId: customerId,
                            testId: TID.OverdueInvoiceRow(topOverdue.id),
                            statusLabel: overdueReason || texts.missingPaymentMethod(),
                            showDivider: true,
                            hideManagePaymentMethods: isArchived
                        })
                    ]
                })
            ]
        })
    });
}
